import { dom } from '@omegagrid/core';
import { msg } from '@omegagrid/localize';
import { NodeSource, TreeAdapter, TreeOptions, TreeSource } from '@omegagrid/tree';
import rp, { Application, ParsedComponentId, GridEditor, EditorContainer } from '@reporting/rp-app-host';
import { SaUsersTreeAdapter, TimesheetGridAdapter, TimesheetMode, TIMESHEET_MODES } from './adapters';

/** Toolbar dropdown source with a fixed list of items. */
class ItemsTreeAdapter extends TreeAdapter {

	constructor(
		private items: NodeSource[],
		private options?: Partial<TreeOptions>,
	) { super() }

	async getTree(): Promise<TreeSource> {
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}
}

const DROPDOWN_OPTIONS: Partial<TreeOptions> = { selectMode: 'single', autoClose: true, checkboxes: false };

/** `users-timesheet-{daily|segments}-{user_name|all}`: the SA activity timesheet grid (P_TIMESHEET). */
export const createTimesheetGrid = async (app: Application, componentId: ParsedComponentId) => {
	const mode = (componentId.ids[1] as TimesheetMode) || 'daily';
	const user = componentId.ids[2];
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new TimesheetGridAdapter(mode, user && user !== SaUsersTreeAdapter.ALL ? user : null);
	return component;
}

/**
 * `users-timesheetContainer`: the timesheet grid with a "Daily totals / Segments" switch and an SA user
 * filter (preset to the logged-in user) in the toolbar; opened from the super module's Logging folder.
 */
export const createTimesheetGridContainer = async (app: Application, componentId: ParsedComponentId) => {
	const defaultMode = TIMESHEET_MODES[0];
	const currentUser = rp.userProvider.user;
	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'mode',
			width: 200,
			type: 'dropdown',
			value: defaultMode.v(),
			caption: msg('View'),
			adapter: new ItemsTreeAdapter(TIMESHEET_MODES.map<NodeSource>(m => ({ k: m.k, v: m.v() })), DROPDOWN_OPTIONS),
		}, {
			id: 'user',
			width: 250,
			type: 'dropdown',
			value: currentUser?.login_name,
			caption: msg('User'),
			adapter: new SaUsersTreeAdapter(true, DROPDOWN_OPTIONS),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			const mode = values['mode'] || defaultMode.k;
			const user = values['user'] || currentUser?.user_name || SaUsersTreeAdapter.ALL;
			return `users-timesheet-${mode}-${user}`;
		}
	});

	return container;
}
